import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { PlateElement, useEditorRef, useReadOnly, useSelected } from 'platejs/react';
import styles from './ImageElement.module.scss';
import { fetchImageUrl, isDocId, uploadSingleImage, registerPendingUpload } from '../../services/imageUploadService';
import { ImageWrapMenu } from '../../toolbar/ImageWrapMenu/ImageWrapMenu';
import { usePlateEditorConfig } from '../../PlateEditorConfigContext';

type Align = 'left' | 'center' | 'right';

export type ImageWrap = 'inline' | 'float-left' | 'float-right' | 'block-center';

const ALIGN_TO_MARGIN: Record<Align, string> = {
    left: '0 auto 0 0',
    center: '0 auto',
    right: '0 0 0 auto',
};

const MIN_WIDTH = 80;
const MAX_WIDTH = 1400;

// Void inline-block image. Selected state surfaces 4 corner resize
// handles and a delete (×) button. Alignment is read from the image
// node's `align` prop (block-level alignment, used when imageWrap is
// 'block-center'). `imageWrap` controls how surrounding text flows
// around the image (inline / float-left / float-right / block-center).
export function ImageElement(props: any) {
    const editor = useEditorRef();
    const selected = useSelected();
    const readOnly = useReadOnly();
    const { uploadSource } = usePlateEditorConfig();

    const rawUrl: string | number | undefined = props.element?.url;
    const storedWidth: number | undefined = props.element?.width;
    const align: Align = (props.element?.align as Align) || 'left';
    const imageWrap: ImageWrap = (props.element?.imageWrap as ImageWrap) || 'block-center';
    const uploadingId: string | undefined = props.element?._uploading;
    const uploadError: boolean = Boolean(props.element?._uploadError);
    const altText: string = typeof props.element?.alt === 'string' ? props.element.alt : '';

    const containerRef = useRef<HTMLDivElement | HTMLSpanElement>(null);
    const [previewWidth, setPreviewWidth] = useState<number | null>(null);
    const [resolvedUrl, setResolvedUrl] = useState<string | null>(typeof rawUrl === 'string' && !isDocId(rawUrl) ? rawUrl : null);
    const [loadError, setLoadError] = useState(false);

    // If the node carries a docId (post-save, post-reload), resolve it to a
    // presigned S3 URL via the shared image cache. base64 / http URLs are
    // used directly without a fetch.
    useEffect(() => {
        setLoadError(false);
        if (typeof rawUrl === 'string' && !isDocId(rawUrl)) {
            setResolvedUrl(rawUrl);
            return;
        }
        if (rawUrl && isDocId(rawUrl)) {
            let cancelled = false;
            fetchImageUrl(String(rawUrl))
                .then((u) => {
                    if (!cancelled) setResolvedUrl(u);
                })
                .catch(() => {
                    if (!cancelled) {
                        setResolvedUrl(null);
                        setLoadError(true);
                    }
                });
            return () => {
                cancelled = true;
            };
        }
        setResolvedUrl(null);
    }, [rawUrl]);

    const url = resolvedUrl;

    const startResize = useCallback(
        (corner: 'tl' | 'tr' | 'bl' | 'br') => (e: React.PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const handle = e.currentTarget as HTMLElement;
            const wrapEl = containerRef.current;
            if (!wrapEl) return;
            const startX = e.clientX;
            const startWidth = wrapEl.offsetWidth;
            const direction = corner === 'tr' || corner === 'br' ? 1 : -1;
            let lastWidth = startWidth;
            handle.setPointerCapture(e.pointerId);
            document.body.style.cursor = corner === 'tr' || corner === 'bl' ? 'nesw-resize' : 'nwse-resize';
            document.body.style.userSelect = 'none';

            const onMove = (ev: PointerEvent) => {
                const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + (ev.clientX - startX) * direction));
                lastWidth = next;
                setPreviewWidth(next);
            };
            const onUp = () => {
                try {
                    handle.releasePointerCapture(e.pointerId);
                } catch {
                    /* noop */
                }
                document.removeEventListener('pointermove', onMove);
                document.removeEventListener('pointerup', onUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                setPreviewWidth(null);
                try {
                    const ed: any = editor;
                    const path = ed?.api?.findPath?.(props.element);
                    if (path) ed?.tf?.setNodes?.({ width: lastWidth }, { at: path });
                } catch {
                    /* defensive: editor not ready */
                }
            };
            document.addEventListener('pointermove', onMove);
            document.addEventListener('pointerup', onUp);
        },
        [editor, props.element],
    );

    const onDelete = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                const ed: any = editor;
                const path = ed?.api?.findPath?.(props.element);
                if (path) ed?.tf?.removeNodes?.({ at: path });
            } catch {
                /* defensive */
            }
        },
        [editor, props.element],
    );

    const onRetryUpload = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const dataUrl = typeof rawUrl === 'string' ? rawUrl : '';
            if (!dataUrl || !dataUrl.startsWith('data:')) return;
            const ed: any = editor;
            const path = ed?.api?.findPath?.(props.element);
            if (!path) return;
            const placeholderId = `retry_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
            try {
                ed?.tf?.setNodes?.({ _uploading: placeholderId, _uploadError: undefined }, { at: path });
            } catch {
                return;
            }
            const promise = uploadSingleImage(dataUrl, uploadSource)
                .then((docId) => {
                    try {
                        const freshPath = ed?.api?.findPath?.(props.element) ?? path;
                        ed?.tf?.setNodes?.({ url: docId, _uploading: undefined, _uploadError: undefined }, { at: freshPath });
                    } catch {
                        /* defensive */
                    }
                })
                .catch((err) => {
                    console.error('[ImageElement] retry upload failed', err);
                    try {
                        const freshPath = ed?.api?.findPath?.(props.element) ?? path;
                        ed?.tf?.setNodes?.({ _uploading: undefined, _uploadError: true }, { at: freshPath });
                    } catch {
                        /* defensive */
                    }
                });
            registerPendingUpload(placeholderId, promise);
        },
        [editor, props.element, rawUrl, uploadSource],
    );

    const renderedWidth = previewWidth ?? storedWidth;

    const imgEl =
        url && !loadError ? (
            <img
                src={url}
                alt={altText}
                className={styles.image}
                draggable
                data-doc-id={rawUrl && isDocId(rawUrl) ? String(rawUrl) : undefined}
                onError={() => setLoadError(true)}
            />
        ) : (
            <div className={styles.placeholder}>{loadError ? 'Image unavailable' : 'Image'}</div>
        );

    const overlay = selected && !readOnly && (
        <>
            <button
                type="button"
                className={styles.deleteBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={onDelete}
                title="Remove image"
                aria-label="Remove image"
            >
                <CloseRoundedIcon fontSize="small" />
            </button>
            <div className={clsx(styles.handle, styles.handleTl)} onPointerDown={startResize('tl')} />
            <div className={clsx(styles.handle, styles.handleTr)} onPointerDown={startResize('tr')} />
            <div className={clsx(styles.handle, styles.handleBl)} onPointerDown={startResize('bl')} />
            <div className={clsx(styles.handle, styles.handleBr)} onPointerDown={startResize('br')} />
            <ImageWrapMenu element={props.element} current={imageWrap} />
        </>
    );

    const uploadOverlay = (
        <>
            {uploadingId && !uploadError && (
                <span className={styles.uploadChip} contentEditable={false} aria-label="Uploading image">
                    <CircularProgress size={12} thickness={5} sx={{ color: '#ffffff' }} />
                    Uploading…
                </span>
            )}
            {uploadError && !readOnly && (
                <button
                    type="button"
                    className={styles.uploadError}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onRetryUpload}
                    title="Retry upload"
                    aria-label="Retry upload"
                >
                    Upload failed — Retry
                </button>
            )}
        </>
    );

    // Float and inline variants need a <span> wrapper so the image sits in
    // the inline text flow. block-center keeps the existing <div> wrapper
    // because PlateElement's parent block is a paragraph (block layout).
    if (imageWrap === 'float-left' || imageWrap === 'float-right') {
        const floatSide = imageWrap === 'float-left' ? 'left' : 'right';
        return (
            <PlateElement {...props} as="span" className={clsx(styles.wrap, styles.wrapFloat)}>
                <span
                    ref={containerRef as React.RefObject<HTMLSpanElement>}
                    contentEditable={false}
                    className={clsx(
                        styles.imageContainer,
                        imageWrap === 'float-left' ? styles.imgFloatLeft : styles.imgFloatRight,
                        selected && styles.imageContainerSelected,
                    )}
                    style={{
                        width: renderedWidth ? `${renderedWidth}px` : undefined,
                        float: floatSide,
                    }}
                >
                    {imgEl}
                    {overlay}
                    {uploadOverlay}
                </span>
                {props.children}
            </PlateElement>
        );
    }

    if (imageWrap === 'inline') {
        return (
            <PlateElement {...props} as="span" className={clsx(styles.wrap, styles.wrapInline)}>
                <span
                    ref={containerRef as React.RefObject<HTMLSpanElement>}
                    contentEditable={false}
                    className={clsx(styles.imageContainer, styles.imgInline, selected && styles.imageContainerSelected)}
                    style={{ width: renderedWidth ? `${renderedWidth}px` : undefined }}
                >
                    {imgEl}
                    {overlay}
                    {uploadOverlay}
                </span>
                {props.children}
            </PlateElement>
        );
    }

    return (
        <PlateElement {...props} as="div" className={styles.wrap} style={{ ...(props.style ?? {}), textAlign: align }}>
            <div
                ref={containerRef as React.RefObject<HTMLDivElement>}
                contentEditable={false}
                className={clsx(styles.imageContainer, selected && styles.imageContainerSelected)}
                style={{
                    width: renderedWidth ? `${renderedWidth}px` : undefined,
                    margin: ALIGN_TO_MARGIN[align],
                }}
            >
                {imgEl}
                {overlay}
                {uploadOverlay}
            </div>
            {props.children}
        </PlateElement>
    );
}
